import { useState } from 'react';
import { Sparkles, Loader2, Check, X, Target, Zap, ChevronDown, ChevronUp, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_OPTIONS = [
  'Ball Control', 'Passing', 'Dribbling', 'First Touch', 'Finishing', 'Shooting',
  'Speed', 'Acceleration', 'Agility', 'Coordination', 'Balance', 'Explosiveness', 'Endurance',
  'Decision Making', 'Positioning', 'Vision', 'Game Awareness', 'Reaction Time'
];

const TECHNICAL_SKILLS = ['Ball Control', 'Passing', 'Dribbling', 'First Touch', 'Finishing', 'Shooting'];
const PHYSICAL_SKILLS = ['Speed', 'Acceleration', 'Agility', 'Coordination', 'Balance', 'Explosiveness', 'Endurance'];
const TACTICAL_SKILLS = ['Positioning', 'Decision Making', 'Vision', 'Game Awareness', 'Reaction Time'];

// Score ranges by level
const SCORE_RANGES = {
  'Focus': { min: 4.0, max: 6.0, label: '4.0 - 6.0' },
  'Developing': { min: 6.1, max: 8.0, label: '6.1 - 8.0' },
  'Proficient': { min: 8.1, max: 10.0, label: '8.1 - 10.0' }
};

// Level colors for badges
const LEVEL_COLORS = {
  'Focus': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  'Developing': { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  'Proficient': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' }
};

/**
 * Meybra AI V3 - Narrative Profile Builder
 * Generates complete athlete development profiles with radar chart values.
 *
 * @param {string} coachType - 'private' | 'team' | 'performance' (or canonical
 *   'private_soccer' / 'private_performance'). Determines the form vocabulary
 *   and which sections are visible:
 *     - team           → match-context labels, all sections visible
 *     - private        → training-context labels, all sections visible
 *     - performance    → physical-only sections (Technical & Tactical hidden)
 */
const AIEvaluationAssistant = ({ athleteName, athletePosition, athleteAge, onApplySuggestions, getAuthHeaders, coachType = 'private' }) => {
  const isTeamCoach = coachType === 'team';
  const isPerformanceCoach = coachType === 'performance' || coachType === 'private_performance';
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [editedScores, setEditedScores] = useState({}); // Track manual score edits
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [activeSection, setActiveSection] = useState('basics');
  
  // V3 Narrative Profile State
  const [formData, setFormData] = useState({
    // BASICS
    age: athleteAge || '',
    sex: '',
    position: athletePosition || 'MID',
    level_context: 'Competitive',
    
    // OVERALL SNAPSHOT
    technical_level: 'On Track',
    physical_level: 'On Track',
    tactical_iq_level: 'On Track',
    
    // TECHNICAL NARRATIVE
    technical_consistency: '',
    dribbling_quality: '',
    first_touch_behavior: '',
    finishing_pattern: '',
    
    // PHYSICAL NARRATIVE
    speed_profile: '',
    acceleration_quality: '',
    movement_efficiency: '',
    physical_stability: '',
    endurance_state: '',
    
    // TACTICAL / IQ NARRATIVE
    positioning_awareness: '',
    decision_behavior: '',
    game_reading: '',
    reaction_speed: '',
    
    // KEYWORDS (optional)
    top_strengths_keywords: [],
    main_focus_keywords: [],
    
    // Additional observations
    coach_observations: ''
  });

  const toggleKeyword = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(v => v !== value) });
    } else if (current.length < 3) {
      setFormData({ ...formData, [field]: [...current, value] });
    } else {
      toast.error('Maximum 3 selections');
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };
      
      const res = await axios.post(`${API}/ai/generate-evaluation`, payload, { 
        headers: getAuthHeaders() 
      });

      setSuggestions(res.data);
      // Initialize editedScores with AI-generated values
      const initialScores = {};
      Object.entries(res.data.skills || {}).forEach(([skillName, skillData]) => {
        initialScores[skillName] = skillData.value || getDefaultScore(skillData.level);
      });
      setEditedScores(initialScores);
      setExpanded(true);
      setShowForm(false);
    } catch (error) {
      console.error('AI error:', error);
      toast.error('Failed to generate evaluation. Please try again.');
    }
    setLoading(false);
  };

  // Get default score for a level
  const getDefaultScore = (level) => {
    const defaults = { 'Focus': 5.0, 'Developing': 7.0, 'Proficient': 8.3 };
    return defaults[level] || 7.0;
  };

  // Handle manual score change with validation
  const handleScoreChange = (skillName, newValue, level) => {
    const range = SCORE_RANGES[level];
    if (!range) return;
    
    let score = parseFloat(newValue);
    if (isNaN(score)) return;
    
    // Clamp to valid range
    score = Math.max(range.min, Math.min(range.max, score));
    // Round to 1 decimal
    score = Math.round(score * 10) / 10;
    
    setEditedScores(prev => ({ ...prev, [skillName]: score }));
  };

  // Validate if score is within level range
  const isScoreValid = (score, level) => {
    const range = SCORE_RANGES[level];
    if (!range) return true;
    return score >= range.min && score <= range.max;
  };

  const handleApply = () => {
    if (suggestions && onApplySuggestions) {
      const legacyFormat = transformToLegacyFormat(suggestions, editedScores);
      onApplySuggestions(legacyFormat);
      toast.success('Evaluation applied! Review and save when ready.');
      setExpanded(false);
    }
  };

  const transformToLegacyFormat = (data, scores = {}) => {
    const technical = {}, physical = {}, tactical = {};
    const skillMapping = {
      'Ball Control': 'ball_control', 'Passing': 'passing', 'Dribbling': 'dribbling',
      'First Touch': 'first_touch', 'Finishing': 'finishing', 'Shooting': 'shooting',
      'Speed': 'speed', 'Acceleration': 'acceleration', 'Agility': 'agility',
      'Coordination': 'coordination', 'Balance': 'balance', 'Explosiveness': 'explosiveness',
      'Endurance': 'endurance', 'Positioning': 'positioning', 'Decision Making': 'decision_making',
      'Vision': 'vision', 'Game Awareness': 'game_awareness', 'Reaction Time': 'reaction_time'
    };
    
    Object.entries(data.skills || {}).forEach(([skillName, skillData]) => {
      const key = skillMapping[skillName] || skillName.toLowerCase().replace(/ /g, '_');
      const value = {
        level: skillData.level,
        score: scores[skillName] || skillData.value || getDefaultScore(skillData.level),
        note: `${skillData.observation || ''}${skillData.next_step ? ' → ' + skillData.next_step : ''}`
      };
      
      if (TECHNICAL_SKILLS.includes(skillName)) technical[key] = value;
      else if (PHYSICAL_SKILLS.includes(skillName)) physical[key] = value;
      else if (TACTICAL_SKILLS.includes(skillName)) tactical[key] = value;
    });
    
    return { technical, physical, tactical };
  };

  const handleDismiss = () => {
    setSuggestions(null);
    setExpanded(false);
    setShowForm(true);
  };

  const getLevelBadge = (level) => LEVEL_COLORS[level] || LEVEL_COLORS['Developing'];

  // Narrative input helper
  const NarrativeSelect = ({ label, value, onChange, options, placeholder }) => (
    <div>
      <label className="text-[10px] text-muted-foreground block mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-8 text-xs bg-background border border-border rounded-sm px-2"
      >
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(opt => (
          <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="bg-gradient-to-r from-primary/5 to-transparent border border-primary/30 rounded-sm p-3 sm:p-4 mb-6 max-w-full overflow-hidden" data-testid="ai-evaluation-assistant">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
          <h3 className="font-heading text-sm uppercase tracking-wider truncate">AI Development Assistant</h3>
          <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded shrink-0">V3</span>
        </div>
        {!showForm && !expanded && (
          <Button size="sm" variant="ghost" onClick={() => setShowForm(true)}>
            New Profile
          </Button>
        )}
      </div>

      {/* NARRATIVE PROFILE BUILDER FORM */}
      {showForm && !expanded && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Build a complete athlete narrative profile. AI will generate a development evaluation with radar chart.
          </p>

          {/* Section Tabs - horizontally scrollable on mobile */}
          <div className="flex gap-1 border-b border-border/30 pb-2 overflow-x-auto -mx-3 px-3 sm:-mx-4 sm:px-4 scrollbar-thin">
            {(isPerformanceCoach
              ? ['basics', 'physical', 'keywords']
              : ['basics', 'technical', 'physical', 'tactical', 'keywords']
            ).map(section => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`px-3 py-1 text-xs rounded-t-sm capitalize transition-colors whitespace-nowrap shrink-0 ${
                  activeSection === section
                    ? 'bg-primary/20 text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {section === 'tactical' ? 'Tactical/IQ' : section}
              </button>
            ))}
          </div>

          {/* BASICS Section */}
          {activeSection === 'basics' && (
            <div className="bg-card/30 rounded-sm p-3 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-1">Age</label>
                  <input
                    type="number"
                    min="4" max="50"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    className="w-full h-8 text-sm bg-background border border-border rounded-sm px-2"
                  />
                </div>
                <NarrativeSelect
                  label="Sex"
                  value={formData.sex}
                  onChange={(v) => setFormData({...formData, sex: v})}
                  options={['Female', 'Male', 'Prefer not to say']}
                  placeholder="--"
                />
                <NarrativeSelect
                  label="Position"
                  value={formData.position}
                  onChange={(v) => setFormData({...formData, position: v})}
                  options={[
                    { value: 'GK', label: 'Goalkeeper (GK)' },
                    { value: 'CB', label: 'Center Back (CB)' },
                    { value: 'LB', label: 'Left Back (LB)' },
                    { value: 'RB', label: 'Right Back (RB)' },
                    { value: 'LWB', label: 'Left Wing Back (LWB)' },
                    { value: 'RWB', label: 'Right Wing Back (RWB)' },
                    { value: 'CDM', label: 'Defensive Midfielder (CDM)' },
                    { value: 'CM', label: 'Central Midfielder (CM)' },
                    { value: 'CAM', label: 'Attacking Midfielder (CAM)' },
                    { value: 'LM', label: 'Left Midfielder (LM)' },
                    { value: 'RM', label: 'Right Midfielder (RM)' },
                    { value: 'LW', label: 'Left Winger (LW)' },
                    { value: 'RW', label: 'Right Winger (RW)' },
                    { value: 'CF', label: 'Center Forward (CF)' },
                    { value: 'ST', label: 'Striker (ST)' }
                  ]}
                />
                <NarrativeSelect
                  label="Level Context"
                  value={formData.level_context}
                  onChange={(v) => setFormData({...formData, level_context: v})}
                  options={['Recreational', 'Competitive', 'Academy']}
                />
              </div>
              
              <div className="border-t border-border/30 pt-3">
                <h4 className="text-[10px] uppercase text-muted-foreground mb-2">Overall Snapshot</h4>
                <div className={`grid gap-3 ${isPerformanceCoach ? 'grid-cols-1' : 'grid-cols-3'}`}>
                  {!isPerformanceCoach && (
                    <NarrativeSelect
                      label="Technical Level"
                      value={formData.technical_level}
                      onChange={(v) => setFormData({...formData, technical_level: v})}
                      options={['Below', 'On Track', 'Above']}
                    />
                  )}
                  <NarrativeSelect
                    label="Physical Level"
                    value={formData.physical_level}
                    onChange={(v) => setFormData({...formData, physical_level: v})}
                    options={['Below', 'On Track', 'Above']}
                  />
                  {!isPerformanceCoach && (
                    <NarrativeSelect
                      label="Tactical/IQ Level"
                      value={formData.tactical_iq_level}
                      onChange={(v) => setFormData({...formData, tactical_iq_level: v})}
                      options={['Below', 'On Track', 'Above']}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TECHNICAL Section — hidden for Performance Coach */}
          {activeSection === 'technical' && !isPerformanceCoach && (
            <div className="bg-card/30 rounded-sm p-3 space-y-3">
              <h4 className="text-xs text-emerald-400 uppercase mb-2">
                Technical Narrative {isTeamCoach ? '(Match Context)' : '(Training Context)'}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NarrativeSelect
                  label={isTeamCoach ? "Technical Performance in Games" : "Technical Consistency in Training"}
                  value={formData.technical_consistency}
                  onChange={(v) => setFormData({...formData, technical_consistency: v})}
                  options={isTeamCoach ? [
                    {value: 'Maintains quality under match pressure', label: 'Maintains quality under match pressure'},
                    {value: 'Confident in possession during games', label: 'Confident in possession during games'},
                    {value: 'Struggles when opponents press', label: 'Struggles when opponents press'},
                    {value: 'Inconsistent between halves', label: 'Inconsistent between halves'},
                    {value: 'Better in low-pressure moments', label: 'Better in low-pressure moments'}
                  ] : [
                    {value: 'Solid under pressure', label: 'Solid under pressure'},
                    {value: 'Confident on the ball', label: 'Confident on the ball'},
                    {value: 'Breaks down when rushed', label: 'Breaks down when rushed'},
                    {value: 'Inconsistent touch', label: 'Inconsistent touch'},
                    {value: 'Needs more repetition', label: 'Needs more repetition'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Dribbling in Match Situations" : "Dribbling Quality"}
                  value={formData.dribbling_quality}
                  onChange={(v) => setFormData({...formData, dribbling_quality: v})}
                  options={isTeamCoach ? [
                    {value: 'Takes on defenders effectively in games', label: 'Takes on defenders effectively'},
                    {value: 'Creates chances through dribbling', label: 'Creates chances through dribbling'},
                    {value: 'Overcomplicates in final third', label: 'Overcomplicates in final third'},
                    {value: 'Loses ball in dangerous areas', label: 'Loses ball in dangerous areas'},
                    {value: 'Prefers safe passing over dribbling', label: 'Prefers safe passing over dribbling'}
                  ] : [
                    {value: 'Creative in 1v1', label: 'Creative in 1v1'},
                    {value: 'Beats defenders', label: 'Beats defenders'},
                    {value: 'Predictable', label: 'Predictable'},
                    {value: 'Loses ball under pressure', label: 'Loses ball under pressure'},
                    {value: 'Safe but limited', label: 'Safe but limited'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "First Touch in Game Play" : "First Touch Behavior"}
                  value={formData.first_touch_behavior}
                  onChange={(v) => setFormData({...formData, first_touch_behavior: v})}
                  options={isTeamCoach ? [
                    {value: 'Controls well under defensive pressure', label: 'Controls well under defensive pressure'},
                    {value: 'Sets up next action quickly', label: 'Sets up next action quickly'},
                    {value: 'Touch lets them down in key moments', label: 'Touch lets them down in key moments'},
                    {value: 'Better receiving to feet than in air', label: 'Better receiving to feet than in air'},
                    {value: 'Nervous when receiving in tight spaces', label: 'Nervous when receiving in tight spaces'}
                  ] : [
                    {value: 'Proactive, sets up next action', label: 'Proactive, sets up next action'},
                    {value: 'Clean and controlled', label: 'Clean and controlled'},
                    {value: 'Reactive', label: 'Reactive'},
                    {value: 'Heavy under pressure', label: 'Heavy under pressure'},
                    {value: 'Inconsistent', label: 'Inconsistent'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Finishing in Games" : "Finishing Pattern"}
                  value={formData.finishing_pattern}
                  onChange={(v) => setFormData({...formData, finishing_pattern: v})}
                  options={isTeamCoach ? [
                    {value: 'Clinical when chances arise', label: 'Clinical when chances arise'},
                    {value: 'Scores in important moments', label: 'Scores in important moments'},
                    {value: 'Misses clear chances often', label: 'Misses clear chances often'},
                    {value: 'Gets in good positions but lacks end product', label: 'Gets in good positions but lacks end product'},
                    {value: 'Shoots when should pass', label: 'Shoots when should pass'}
                  ] : [
                    {value: 'Clinical', label: 'Clinical'},
                    {value: 'Calm in front of goal', label: 'Calm in front of goal'},
                    {value: 'Hesitates', label: 'Hesitates'},
                    {value: 'Rushed', label: 'Rushed'},
                    {value: 'Lacks composure', label: 'Lacks composure'}
                  ]}
                />
              </div>
            </div>
          )}

          {/* PHYSICAL Section */}
          {activeSection === 'physical' && (
            <div className="bg-card/30 rounded-sm p-3 space-y-3">
              <h4 className="text-xs text-blue-400 uppercase mb-2">
                Physical Narrative {isTeamCoach ? '(Match Context)' : '(Training Context)'}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NarrativeSelect
                  label={isTeamCoach ? "Speed in Match Situations" : "Speed Profile"}
                  value={formData.speed_profile}
                  onChange={(v) => setFormData({...formData, speed_profile: v})}
                  options={isTeamCoach ? [
                    {value: 'Wins foot races consistently', label: 'Wins foot races consistently'},
                    {value: 'Uses speed effectively in transitions', label: 'Uses speed effectively in transitions'},
                    {value: 'Quick but lacks game intelligence to use it', label: 'Quick but lacks game intelligence'},
                    {value: 'Gets beaten in sprints', label: 'Gets beaten in sprints'},
                    {value: 'Pace not suited to current position', label: 'Pace not suited to position'}
                  ] : [
                    {value: 'Quick in open space', label: 'Quick in open space'},
                    {value: 'Above average pace', label: 'Above average pace'},
                    {value: 'Average', label: 'Average'},
                    {value: 'Slow to accelerate', label: 'Slow to accelerate'},
                    {value: 'Not a strength', label: 'Not a strength'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Acceleration in Games" : "Acceleration Quality"}
                  value={formData.acceleration_quality}
                  onChange={(v) => setFormData({...formData, acceleration_quality: v})}
                  options={isTeamCoach ? [
                    {value: 'Explosive starts create separation', label: 'Explosive starts create separation'},
                    {value: 'First step advantage in duels', label: 'First step advantage in duels'},
                    {value: 'Slow to react to transitions', label: 'Slow to react to transitions'},
                    {value: 'Opponents get jump on them', label: 'Opponents get jump on them'}
                  ] : [
                    {value: 'Explosive first steps', label: 'Explosive first steps'},
                    {value: 'Quick off the mark', label: 'Quick off the mark'},
                    {value: 'Average', label: 'Average'},
                    {value: 'Delayed first steps', label: 'Delayed first steps'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Movement During Play" : "Movement Efficiency"}
                  value={formData.movement_efficiency}
                  onChange={(v) => setFormData({...formData, movement_efficiency: v})}
                  options={isTeamCoach ? [
                    {value: 'Intelligent off-ball movement', label: 'Intelligent off-ball movement'},
                    {value: 'Creates space for teammates', label: 'Creates space for teammates'},
                    {value: 'Static, needs to move more', label: 'Static, needs to move more'},
                    {value: 'Runs but not purposefully', label: 'Runs but not purposefully'}
                  ] : [
                    {value: 'Fluid movement', label: 'Fluid movement'},
                    {value: 'Efficient', label: 'Efficient'},
                    {value: 'Mechanical', label: 'Mechanical'},
                    {value: 'Needs coordination work', label: 'Needs coordination work'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Physical Duels" : "Physical Stability"}
                  value={formData.physical_stability}
                  onChange={(v) => setFormData({...formData, physical_stability: v})}
                  options={isTeamCoach ? [
                    {value: 'Wins most physical battles', label: 'Wins most physical battles'},
                    {value: 'Holds off opponents effectively', label: 'Holds off opponents effectively'},
                    {value: 'Gets knocked off ball too easily', label: 'Gets knocked off ball too easily'},
                    {value: 'Avoids physical confrontations', label: 'Avoids physical confrontations'}
                  ] : [
                    {value: 'Strong balance', label: 'Strong balance'},
                    {value: 'Stable through contact', label: 'Stable through contact'},
                    {value: 'Average', label: 'Average'},
                    {value: 'Loses posture', label: 'Loses posture'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Match Stamina" : "Endurance State"}
                  value={formData.endurance_state}
                  onChange={(v) => setFormData({...formData, endurance_state: v})}
                  options={isTeamCoach ? [
                    {value: 'Consistent performance full 90 minutes', label: 'Consistent performance full 90 min'},
                    {value: 'Strong until final 15 minutes', label: 'Strong until final 15 minutes'},
                    {value: 'Performance drops in second half', label: 'Performance drops in second half'},
                    {value: 'Struggles to maintain intensity', label: 'Struggles to maintain intensity'},
                    {value: 'Needs to be substituted early', label: 'Needs to be substituted early'}
                  ] : [
                    {value: 'Maintains intensity', label: 'Maintains intensity'},
                    {value: 'Good stamina', label: 'Good stamina'},
                    {value: 'Average', label: 'Average'},
                    {value: 'Drops after 30min', label: 'Drops after 30min'},
                    {value: 'Tires quickly', label: 'Tires quickly'}
                  ]}
                />
              </div>
            </div>
          )}

          {/* TACTICAL / IQ Section — hidden for Performance Coach */}
          {activeSection === 'tactical' && !isPerformanceCoach && (
            <div className="bg-card/30 rounded-sm p-3 space-y-3">
              <h4 className="text-xs text-yellow-400 uppercase mb-2">
                Tactical / IQ Narrative {isTeamCoach ? '(Match Context)' : '(Training Context)'}
              </h4>
              {!isTeamCoach && (
                <p className="text-[10px] text-muted-foreground leading-relaxed -mt-1 mb-2">
                  Trainable cognitive behaviours observed during drills and small groups.
                  These map to Vision · Game Awareness · Decision Making · Positioning · Reaction Time.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <NarrativeSelect
                  label={isTeamCoach ? "Positioning in Games" : "Scanning Behavior"}
                  value={formData.positioning_awareness}
                  onChange={(v) => setFormData({...formData, positioning_awareness: v})}
                  options={isTeamCoach ? [
                    {value: 'Always in right place at right time', label: 'Always in right place at right time'},
                    {value: 'Understands team shape well', label: 'Understands team shape well'},
                    {value: 'Positioning improves as game goes on', label: 'Positioning improves as game goes on'},
                    {value: 'Gets caught out of position often', label: 'Gets caught out of position often'},
                    {value: 'Struggles with defensive positioning', label: 'Struggles with defensive positioning'}
                  ] : [
                    {value: 'Elite awareness habits — scans before every action', label: 'Elite awareness habits — scans before every action'},
                    {value: 'Proactive scanning before receiving', label: 'Proactive scanning before receiving'},
                    {value: 'Consistent scans during drills', label: 'Consistent scans during drills'},
                    {value: 'Occasional scans — head still drops to ball', label: 'Occasional scans — head still drops to ball'},
                    {value: 'Rarely scans — head down most reps', label: 'Rarely scans — head down most reps'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Decision Making in Matches" : "Decision Speed"}
                  value={formData.decision_behavior}
                  onChange={(v) => setFormData({...formData, decision_behavior: v})}
                  options={isTeamCoach ? [
                    {value: 'Makes right choice under pressure', label: 'Makes right choice under pressure'},
                    {value: 'Good decisions in final third', label: 'Good decisions in final third'},
                    {value: 'Decisions slow down in big moments', label: 'Decisions slow down in big moments'},
                    {value: 'Too many turnovers from poor choices', label: 'Too many turnovers from poor choices'},
                    {value: 'Plays too safe when team needs creativity', label: 'Plays too safe when team needs creativity'}
                  ] : [
                    {value: 'Elite processing speed — decides before receiving', label: 'Elite processing speed — decides before receiving'},
                    {value: 'Quick reads — picks the right option fast', label: 'Quick reads — picks the right option fast'},
                    {value: 'Appropriate — decisions land on time', label: 'Appropriate — decisions land on time'},
                    {value: 'Hesitant — second guesses in 1v1 reps', label: 'Hesitant — second guesses in 1v1 reps'},
                    {value: 'Slow decisions — reads come late', label: 'Slow decisions — reads come late'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "Game Intelligence" : "Pattern Recognition"}
                  value={formData.game_reading}
                  onChange={(v) => setFormData({...formData, game_reading: v})}
                  options={isTeamCoach ? [
                    {value: 'Reads the game exceptionally well', label: 'Reads the game exceptionally well'},
                    {value: 'Anticipates opponents movements', label: 'Anticipates opponents movements'},
                    {value: 'Adapts to game situations', label: 'Adapts to game situations'},
                    {value: 'Slow to adapt tactically', label: 'Slow to adapt tactically'},
                    {value: 'Struggles to read game flow', label: 'Struggles to read game flow'}
                  ] : [
                    {value: 'Elite anticipation — sees the solution before the rep starts', label: 'Elite anticipation — sees the solution before the rep starts'},
                    {value: 'Advanced — solves new drills quickly', label: 'Advanced — solves new drills quickly'},
                    {value: 'Functional — recognises common patterns', label: 'Functional — recognises common patterns'},
                    {value: 'Emerging — needs repetition to lock the cue', label: 'Emerging — needs repetition to lock the cue'},
                    {value: 'Struggles to identify solutions', label: 'Struggles to identify solutions'}
                  ]}
                />
                <NarrativeSelect
                  label={isTeamCoach ? "In-Game Reactions" : "Instruction Retention"}
                  value={formData.reaction_speed}
                  onChange={(v) => setFormData({...formData, reaction_speed: v})}
                  options={isTeamCoach ? [
                    {value: 'Quick to react to loose balls', label: 'Quick to react to loose balls'},
                    {value: 'Reacts well to transitions', label: 'Reacts well to transitions'},
                    {value: 'Slow to transition defensively', label: 'Slow to transition defensively'},
                    {value: 'Often caught ball-watching', label: 'Often caught ball-watching'}
                  ] : [
                    {value: 'Elite retention — locks coaching cues first rep', label: 'Elite retention — locks coaching cues first rep'},
                    {value: 'Sharp — applies new instructions same session', label: 'Sharp — applies new instructions same session'},
                    {value: 'Average — needs 2–3 reps to dial it in', label: 'Average — needs 2–3 reps to dial it in'},
                    {value: 'Inconsistent — reverts to old habits under fatigue', label: 'Inconsistent — reverts to old habits under fatigue'},
                    {value: 'Slow to internalise coaching cues', label: 'Slow to internalise coaching cues'}
                  ]}
                />
              </div>
            </div>
          )}

          {/* KEYWORDS Section */}
          {activeSection === 'keywords' && (
            <div className="bg-card/30 rounded-sm p-3 space-y-4">
              <p className="text-[9px] text-muted-foreground">
                Optional hints for ranking. These do NOT set levels automatically.
              </p>
              
              {/* Focus Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-3 w-3 text-red-400" />
                  <label className="text-[10px] text-muted-foreground">Focus Areas (max 3)</label>
                  <span className="text-[10px] text-red-400">{formData.main_focus_keywords.length}/3</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {SKILL_OPTIONS.map(skill => (
                    <button
                      key={`focus-${skill}`}
                      type="button"
                      onClick={() => toggleKeyword('main_focus_keywords', skill)}
                      disabled={formData.top_strengths_keywords.includes(skill)}
                      className={`px-2 py-0.5 text-[10px] rounded-sm border transition-colors ${
                        formData.main_focus_keywords.includes(skill)
                          ? 'bg-red-500/20 text-red-400 border-red-500/30'
                          : formData.top_strengths_keywords.includes(skill)
                          ? 'bg-muted/30 text-muted-foreground/50 border-transparent cursor-not-allowed'
                          : 'bg-muted/50 text-muted-foreground border-transparent hover:border-red-500/30'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              {/* Strengths Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-3 w-3 text-emerald-400" />
                  <label className="text-[10px] text-muted-foreground">Top Strengths (max 3)</label>
                  <span className="text-[10px] text-emerald-400">{formData.top_strengths_keywords.length}/3</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {SKILL_OPTIONS.map(skill => (
                    <button
                      key={`strength-${skill}`}
                      type="button"
                      onClick={() => toggleKeyword('top_strengths_keywords', skill)}
                      disabled={formData.main_focus_keywords.includes(skill)}
                      className={`px-2 py-0.5 text-[10px] rounded-sm border transition-colors ${
                        formData.top_strengths_keywords.includes(skill)
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                          : formData.main_focus_keywords.includes(skill)
                          ? 'bg-muted/30 text-muted-foreground/50 border-transparent cursor-not-allowed'
                          : 'bg-muted/50 text-muted-foreground border-transparent hover:border-emerald-500/30'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Observations */}
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">Additional Observations (optional)</label>
                <Textarea
                  value={formData.coach_observations}
                  onChange={(e) => setFormData({...formData, coach_observations: e.target.value})}
                  placeholder="Any other observations not covered above..."
                  className="bg-background text-sm"
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Generate Button */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-[10px] text-muted-foreground">
              AI generates • Coach reviews • Coach saves
            </p>
            <Button 
              onClick={handleGenerate} 
              disabled={loading}
              className="bg-primary text-black"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Profile
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* RESULTS VIEW */}
      {expanded && suggestions && (
        <div className="space-y-4">
          {/* Action Bar */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Review the evaluation. Click "Apply" to fill the form.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                <X className="h-4 w-4 mr-1" /> Dismiss
              </Button>
              <Button size="sm" onClick={handleApply} className="bg-primary text-black">
                <Check className="h-4 w-4 mr-1" /> Apply to Form
              </Button>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-card/50 rounded-sm p-3">
            <p className="text-sm font-medium">{suggestions.summary_line}</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-2">
                <div className="flex items-center gap-1 mb-2">
                  <Zap className="h-3 w-3 text-emerald-400" />
                  <span className="text-[10px] uppercase text-emerald-400">Top Strengths</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(suggestions.top_strengths || []).map((skill, i) => (
                    <span key={i} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-2">
                <div className="flex items-center gap-1 mb-2">
                  <Target className="h-3 w-3 text-red-400" />
                  <span className="text-[10px] uppercase text-red-400">Focus Areas</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(suggestions.focus_areas || []).map((skill, i) => (
                    <span key={i} className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Score Ranges Info */}
          <div className="bg-muted/30 rounded-sm p-2 flex items-center justify-center gap-4 text-[9px]">
            <span className="text-red-400">Focus: 4.0-6.0</span>
            <span className="text-amber-400">Developing: 6.1-8.0</span>
            <span className="text-emerald-400">Proficient: 8.1-10.0</span>
            <span className="text-muted-foreground ml-2">| Click score to edit</span>
          </div>

          {/* Skills Grid with Editable Scores */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-h-[450px] overflow-y-auto">
            {/* Technical */}
            <div className="bg-card/50 p-3 rounded-sm">
              <h4 className="text-xs uppercase text-emerald-400 tracking-wider mb-3 font-medium">Technical</h4>
              <div className="space-y-2">
                {TECHNICAL_SKILLS.map(skillName => {
                  const skill = suggestions.skills?.[skillName];
                  if (!skill) return null;
                  const colors = getLevelBadge(skill.level);
                  const range = SCORE_RANGES[skill.level];
                  const currentScore = editedScores[skillName] ?? skill.value ?? getDefaultScore(skill.level);
                  return (
                    <div key={skillName} className="bg-background/50 p-2 rounded-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">{skillName}</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                            {skill.level}
                          </span>
                          <div className="flex items-center gap-1 bg-muted/50 rounded px-1.5 py-0.5">
                            <input
                              type="number"
                              min={range?.min || 4}
                              max={range?.max || 10}
                              step="0.1"
                              value={currentScore}
                              onChange={(e) => handleScoreChange(skillName, e.target.value, skill.level)}
                              className="w-12 h-5 text-[11px] text-center bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-primary rounded"
                              data-testid={`score-input-${skillName.toLowerCase().replace(/ /g, '-')}`}
                            />
                            <span className="text-[8px] text-muted-foreground">({range?.label})</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{skill.observation}</p>
                      {skill.next_step && (
                        <p className="text-[10px] text-primary/80 mt-1">→ {skill.next_step}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Physical */}
            <div className="bg-card/50 p-3 rounded-sm">
              <h4 className="text-xs uppercase text-blue-400 tracking-wider mb-3 font-medium">Physical</h4>
              <div className="space-y-2">
                {PHYSICAL_SKILLS.map(skillName => {
                  const skill = suggestions.skills?.[skillName];
                  if (!skill) return null;
                  const colors = getLevelBadge(skill.level);
                  const range = SCORE_RANGES[skill.level];
                  const currentScore = editedScores[skillName] ?? skill.value ?? getDefaultScore(skill.level);
                  return (
                    <div key={skillName} className="bg-background/50 p-2 rounded-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">{skillName}</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                            {skill.level}
                          </span>
                          <div className="flex items-center gap-1 bg-muted/50 rounded px-1.5 py-0.5">
                            <input
                              type="number"
                              min={range?.min || 4}
                              max={range?.max || 10}
                              step="0.1"
                              value={currentScore}
                              onChange={(e) => handleScoreChange(skillName, e.target.value, skill.level)}
                              className="w-12 h-5 text-[11px] text-center bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-primary rounded"
                              data-testid={`score-input-${skillName.toLowerCase().replace(/ /g, '-')}`}
                            />
                            <span className="text-[8px] text-muted-foreground">({range?.label})</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{skill.observation}</p>
                      {skill.next_step && (
                        <p className="text-[10px] text-primary/80 mt-1">→ {skill.next_step}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tactical */}
            <div className="bg-card/50 p-3 rounded-sm">
              <h4 className="text-xs uppercase text-yellow-400 tracking-wider mb-3 font-medium">Tactical / IQ</h4>
              <div className="space-y-2">
                {TACTICAL_SKILLS.map(skillName => {
                  const skill = suggestions.skills?.[skillName];
                  if (!skill) return null;
                  const colors = getLevelBadge(skill.level);
                  const range = SCORE_RANGES[skill.level];
                  const currentScore = editedScores[skillName] ?? skill.value ?? getDefaultScore(skill.level);
                  return (
                    <div key={skillName} className="bg-background/50 p-2 rounded-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">{skillName}</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                            {skill.level}
                          </span>
                          <div className="flex items-center gap-1 bg-muted/50 rounded px-1.5 py-0.5">
                            <input
                              type="number"
                              min={range?.min || 4}
                              max={range?.max || 10}
                              step="0.1"
                              value={currentScore}
                              onChange={(e) => handleScoreChange(skillName, e.target.value, skill.level)}
                              className="w-12 h-5 text-[11px] text-center bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-primary rounded"
                              data-testid={`score-input-${skillName.toLowerCase().replace(/ /g, '-')}`}
                            />
                            <span className="text-[8px] text-muted-foreground">({range?.label})</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{skill.observation}</p>
                      {skill.next_step && (
                        <p className="text-[10px] text-primary/80 mt-1">→ {skill.next_step}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIEvaluationAssistant;
